import axios from 'axios'
import { tokenStorage } from '../utils/crypto'
import apiConfig from '../config/apiConfig'

// API Configuration
const API_BASE_URL = apiConfig.API_BASE_URL

/**
 * Create axios instance with default configuration
 */
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds timeout
})

/**
 * Request interceptor - Add auth token and handle workspace switching
 */
api.interceptors.request.use(
  (config) => {
    // Add authentication token
    const token = tokenStorage.getToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Handle FormData - remove Content-Type to let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }

    // Add workspace switching parameter for all requests — including notification
    // endpoints. Notifications are workspace-scoped: each workspace has its own
    // tray, so the current owner_account_id must be forwarded or followers would
    // only ever see notifications from their own workspace.
    const userInfo = tokenStorage.getUserInfo()

    if (userInfo && userInfo.ownerAccountId) {
      config.params = {
        ...config.params,
        owner_account_id: userInfo.ownerAccountId
      }
    }

    // Log API requests in development
    if (import.meta.env.DEV) {
      console.log('🔍 API Request:', config.method?.toUpperCase(), config.url, {
        params: config.params,
        data: config.data instanceof FormData ? '[FormData]' : config.data,
        userInfo: userInfo,
        ownerAccountId: userInfo?.ownerAccountId
      })
    }

    return config
  },
  (error) => {
    console.error('Request interceptor error:', error)
    return Promise.reject(error)
  }
)

/**
 * Response interceptor - Handle common errors and token expiration
 */
api.interceptors.response.use(
  (response) => {
    // Log API responses in development
    if (import.meta.env.DEV) {
      console.log('API Response:', response.config.method?.toUpperCase(), response.config.url, response.data)
    }

    return response
  },
  (error) => {
    // Handle common error scenarios
    if (error.response) {
      const { status, data } = error.response

      // Token expired or invalid
      if (status === 401) {
        console.error('Authentication failed - token expired or invalid')
        tokenStorage.removeUserInfo()
        // Redirect to login if not already there
        if (window.location.pathname !== '/login') {
          window.location.href = '/login'
        }
      }

      // Server errors
      if (status >= 500) {
        console.error('Server error:', status, data)
      }

      // Phase 3 — Drive-First gate-blocked response (402 Payment Required)
      // Backend returns { type: 'error', code: 402, data: { gate: GateResult } }
      // We dispatch a window event so the global GateModalHost can render the
      // appropriate dialog with the right message + CTA.
      if (status === 402 && data?.data?.gate) {
        try {
          window.dispatchEvent(new CustomEvent('ovacs:gate_blocked', { detail: data.data.gate }))
        } catch (e) { /* non-fatal */ }
      }

      // Return structured error
      return Promise.reject({
        status,
        message: data?.message || 'An error occurred',
        data: data?.data || null,
        type: data?.type || 'error'
      })
    }

    // Network errors
    if (error.code === 'ECONNABORTED') {
      return Promise.reject({
        status: 408,
        message: 'Request timeout - please try again',
        type: 'timeout'
      })
    }

    // Other errors
    console.error('API Error:', error)
    return Promise.reject({
      status: 0,
      message: 'Network error - please check your connection',
      type: 'network'
    })
  }
)

/**
 * Generic API request wrapper with error handling
 */
const apiRequest = async (method, url, data = null, config = {}) => {
  try {
    const response = await api({
      method,
      url,
      data: data || undefined,
      ...config
    })

    return {
      success: true,
      data: response.data,
      status: response.status
    }
  } catch (error) {
    return {
      success: false,
      error: {
        message: error.message || 'Request failed',
        status: error.status || 0,
        type: error.type || 'error'
      }
    }
  }
}

/**
 * API service methods
 */
export const apiService = {
  // Generic methods
  get: (url, config = {}) => {
    const { params, ...restConfig } = config;
    return apiRequest('get', url, null, { params, ...restConfig });
  },
  
  post: (url, data = {}, config = {}) => 
    apiRequest('post', url, data, config),
  
  put: (url, data = {}, config = {}) => 
    apiRequest('put', url, data, config),
  
  patch: (url, data = {}, config = {}) => 
    apiRequest('patch', url, data, config),
  
  delete: (url, config = {}) => 
    apiRequest('delete', url, null, config),

  // File upload helper
  uploadFiles: (url, files, additionalData = {}, onProgress = null) => {
    const formData = new FormData()
    
    // Add files
    if (Array.isArray(files)) {
      files.forEach((file, index) => {
        formData.append(`files`, file)
      })
    } else {
      formData.append('files', files)
    }

    // Add additional data
    Object.keys(additionalData).forEach(key => {
      formData.append(key, additionalData[key])
    })

    return apiRequest('post', url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: onProgress ? (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        onProgress(percentCompleted)
      } : undefined
    })
  },

  // Workspace switching helper
  withWorkspace: (ownerAccountId) => ({
    get: (url, params = {}, config = {}) => 
      apiService.get(url, { ...params, owner_account_id: ownerAccountId }, config),
    
    post: (url, data = {}, config = {}) => 
      apiService.post(url, { ...data, owner_account_id: ownerAccountId }, config),
    
    put: (url, data = {}, config = {}) => 
      apiService.put(url, { ...data, owner_account_id: ownerAccountId }, config),
    
    patch: (url, data = {}, config = {}) => 
      apiService.patch(url, { ...data, owner_account_id: ownerAccountId }, config),
    
    delete: (url, config = {}) => 
      apiService.delete(url, { ...config, params: { owner_account_id: ownerAccountId } }),
  })
}

/**
 * Unified Document API — covers all types: document, text message, voice message
 */
export const documentAPI = {
  // List/filter documents (all types by default, use ?type= to filter)
  list: (params = {}) => apiService.get('/documents/', { params }),

  // Create any document type
  // type='document': use FormData with files[]
  // type='text': use JSON with content
  // type='voice': use FormData with voice_file
  create: (data, onProgress) => {
    if (data instanceof FormData) {
      return apiService.post('/documents/create/', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: onProgress,
      })
    }
    return apiService.post('/documents/create/', data)
  },

  // Upload file documents (backward compat shorthand)
  uploadFiles: (files, additionalData, onProgress) =>
    apiService.uploadFiles('/documents/upload/', files, additionalData, onProgress),

  // Get single document (any type)
  get: (id, params = {}) => apiService.get(`/documents/${id}/`, { params }),

  // Update document (any type)
  update: (id, data, params = {}) => apiService.put(`/documents/${id}/`, data, { params }),

  // Delete document (any type)
  delete: (id, params = {}) => apiService.delete(`/documents/${id}/`, { params }),

  // Mark as read
  markAsRead: (id) => apiService.post(`/documents/${id}/mark-read/`),

  // Comments (any document type)
  getComments: (id, params = {}) => apiService.get(`/documents/${id}/comments/`, { params }),
  postComment: (id, content, params = {}) =>
    apiService.post(`/documents/${id}/comments/`, { content }, { params }),
  deleteComment: (id, commentId, params = {}) =>
    apiService.delete(`/documents/${id}/comments/${commentId}/`, { params }),

  // Seen tracking (explicit view/download signal) + seen-by list
  recordSeen: (id, action, params = {}) =>
    apiService.post(`/documents/${id}/seen/`, { action }, { params }),
  getSeenBy: (id, params = {}) => apiService.get(`/documents/${id}/seen-by/`, { params }),

  // Move to group
  moveToGroup: (id, groupId, params = {}) =>
    apiService.patch(`/documents/${id}/move-to-group/`, { group_id: groupId }, { params }),

  // Document Groups
  listGroups: (params = {}) => apiService.get('/document-groups/', { params }),
  createGroup: (data, params = {}) => apiService.post('/document-groups/', data, { params }),
  getGroup: (id, params = {}) => apiService.get(`/document-groups/${id}/`, { params }),
  deleteGroup: (id, params = {}) => apiService.delete(`/document-groups/${id}/`, { params }),

  // Signed URLs
  generateSignedUrls: (data) => apiService.post('/documents/generate-signed-urls/', data),
}

/**
 * Message API — alias for documentAPI filtered to type=text,voice
 * Kept for backward compatibility with existing components
 */
export const messageAPI = {
  getMessages: (params = {}) => documentAPI.list({ type: 'text,voice', ...params }),
  createMessage: (data) => documentAPI.create(data),
  getMessage: (id) => documentAPI.get(id),
  updateMessage: (id, data) => documentAPI.update(id, data),
  deleteMessage: (id) => documentAPI.delete(id),
  markAsRead: (id) => documentAPI.markAsRead(id),
  getCaseMessages: (caseId, params = {}) =>
    documentAPI.list({ ...params, case_id: caseId, type: 'text,voice' }),
  createCaseMessage: (caseId, data) =>
    documentAPI.create(data instanceof FormData ? data : { ...data, case_id: caseId }),
}

/**
 * Notification Preference API methods
 */
export const notificationPreferenceAPI = {
  getPreferences: (params = {}) => apiService.get('/notification-preferences/', { params }),
  updatePreferences: (data, params = {}) => apiService.put('/notification-preferences/', data, { params }),
}

/**
 * Notification API methods (LEGACY - will be deprecated)
 * Use ovacsNotificationAPI instead
 */
export const notificationAPI = {
  // Get all notifications
  getNotifications: (params = {}) =>
    apiService.get('/system-notifications/', { params }),

  // Mark notification as read
  markAsRead: (id) =>
    apiService.post(`/system-notifications/${id}/mark-read/`),

  // Mark all notifications as read
  markAllAsRead: () =>
    apiService.post('/system-notifications/mark-all-read/')
}

/**
 * Ovacs Unified Notification System API
 *
 * Complete API for the unified notification system supporting:
 * - Real-time SSE streaming
 * - Pagination and filtering
 * - Read/unread management
 * - Bulk actions
 * - Priority and type filtering
 * - Statistics
 */
export const ovacsNotificationAPI = {
  /**
   * Get paginated list of notifications with optional filters
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.page_size - Items per page (default: 20, max: 100)
   * @param {string} params.notification_type - Filter by type (message, broadcast, etc.)
   * @param {string} params.priority - Filter by priority (low, medium, high, urgent)
   * @param {boolean} params.is_read - Filter by read status
   * @param {string} params.search - Search in title and message
   * @param {boolean} params.exclude_expired - Exclude expired notifications
   * @returns {Promise} Response with {count, next, previous, unread_count, results}
   */
  getNotifications: (params = {}) =>
    apiService.get('/ovacs-notifications/', { params }),

  /**
   * Get single notification by ID
   * @param {number} id - Notification ID
   * @returns {Promise} Notification object
   */
  getNotification: (id) =>
    apiService.get(`/ovacs-notifications/${id}/`),

  /**
   * Get unread notification count
   * @returns {Promise} {unread_count: number}
   */
  getUnreadCount: () =>
    apiService.get('/ovacs-notifications/unread-count/'),

  /**
   * Get notification statistics
   * @returns {Promise} {total, unread, by_type, by_priority}
   */
  getStats: () =>
    apiService.get('/ovacs-notifications/stats/'),

  /**
   * Mark single notification as read
   * @param {number} id - Notification ID
   * @returns {Promise} Updated notification
   */
  markAsRead: (id) =>
    apiService.post(`/ovacs-notifications/${id}/mark-read/`),

  /**
   * Mark single notification as unread
   * @param {number} id - Notification ID
   * @returns {Promise} Updated notification
   */
  markAsUnread: (id) =>
    apiService.post(`/ovacs-notifications/${id}/mark-unread/`),

  /**
   * Mark all notifications as read
   * @returns {Promise} {message, count}
   */
  markAllAsRead: () =>
    apiService.post('/ovacs-notifications/mark-all-read/'),

  /**
   * Bulk action on multiple notifications
   * @param {Array<number>} notificationIds - Array of notification IDs
   * @param {string} action - Action: 'mark_read', 'mark_unread', 'delete'
   * @returns {Promise} {message, count}
   */
  bulkAction: (notificationIds, action) =>
    apiService.post('/ovacs-notifications/bulk-action/', {
      notification_ids: notificationIds,
      action: action
    }),

  /**
   * Delete single notification
   * @param {number} id - Notification ID
   * @returns {Promise} Empty response
   */
  deleteNotification: (id) =>
    apiService.delete(`/ovacs-notifications/${id}/delete/`),

  /**
   * Helper: Get SSE stream URL
   * @param {string} token - JWT token
   * @param {number} ownerAccountId - Owner account ID for workspace
   * @returns {string} SSE stream URL
   */
  getStreamURL: (token, ownerAccountId) => {
    const baseURL = apiService.defaults.baseURL || ''
    let url = `${baseURL}/ovacs-notifications/stream/`
    if (ownerAccountId) {
      url += `?owner_account_id=${ownerAccountId}`
    }
    url += `${ownerAccountId ? '&' : '?'}token=${token}`
    return url
  }
}

/**
 * Superadmin API methods
 * Requires user role='superadmin'
 */
export const superadminAPI = {
  // Statistics
  getStatistics: () => apiService.get('/superadmin/statistics/'),
  getInactiveAccounts: (days = 7) => 
    apiService.get('/superadmin/inactive-accounts/', { params: { days } }),
  
  // Account Management
  getAccounts: (params = {}) => apiService.get('/superadmin/accounts/', { params }),
  getAccountDetail: (accountId) => apiService.get(`/superadmin/accounts/${accountId}/`),
  accountAction: (accountId, action, reason = '') => 
    apiService.post(`/superadmin/accounts/${accountId}/action/`, { action, reason }),
  
  // Subscription Management
  cancelSubscription: (subscriptionId) => 
    apiService.post(`/superadmin/subscriptions/${subscriptionId}/cancel/`),
  
  // Communication
  broadcastNotification: (data) => 
    apiService.post('/superadmin/notifications/broadcast/', data),
  sendPaymentReminders: () => 
    apiService.post('/superadmin/notifications/payment-reminder/'),
  
  // Contact Messages
  getContactMessages: (params = {}) => 
    apiService.get('/superadmin/contact-messages/', { params }),
  replyToContactMessage: (messageId, replyText) => 
    apiService.post(`/superadmin/contact-messages/${messageId}/reply/`, { reply_text: replyText }),
  
  // Data Export
  exportOwners: () => apiService.get('/superadmin/export/owners/', { responseType: 'blob' }),
  exportEmployees: () => apiService.get('/superadmin/export/employees/', { responseType: 'blob' }),

  // Permission Management
  getPermissionMatrix: () => apiService.get('/superadmin/permissions/role-matrix/'),
  updatePermissionMatrix: (data) => apiService.put('/superadmin/permissions/role-matrix/', data),
  getSecurityRules: () => apiService.get('/superadmin/permissions/security-rules/'),
  updateSecurityRules: (data) => apiService.put('/superadmin/permissions/security-rules/', data),

  // Dashboard widgets (Phase 2)
  getCloudStorageStats: () => apiService.get('/superadmin/cloud-storage/stats/'),
  getActivityFeed: (hours = 24) => apiService.get('/superadmin/activity/recent/', { params: { hours } }),
  getTopAccounts: (limit = 10) => apiService.get('/superadmin/dashboard/top-accounts/', { params: { limit } }),

  // Phase 3 — subscription management
  listSubscriptions: (params = {}) => apiService.get('/superadmin/subscriptions/list/', { params }),
  getSubscription: (id) => apiService.get(`/superadmin/subscriptions/${id}/`),
  extendTrial: (id, days) => apiService.post(`/superadmin/subscriptions/${id}/extend-trial/`, { days }),
  forceBlockSubscription: (id) => apiService.post(`/superadmin/subscriptions/${id}/force-block/`),
  unblockSubscription: (id, reason = '') => apiService.post(`/superadmin/subscriptions/${id}/unblock/`, { reason }),
  setSubscriptionOverride: (id, override) => apiService.post(`/superadmin/subscriptions/${id}/set-override/`, { override }),
  forceCancelSubscription: (id, reason = '') => apiService.post(`/superadmin/subscriptions/${id}/force-cancel/`, { reason }),
  refundSubscription: (id, opts = {}) => apiService.post(`/superadmin/subscriptions/${id}/refund/`, opts),
  getBlockPolicy: () => apiService.get('/superadmin/block-policy/'),
  updateBlockPolicy: (changes) => apiService.patch('/superadmin/block-policy/', changes),
  listSubscriptionPlans: () => apiService.get('/superadmin/subscription-plans/'),
  updateSubscriptionPlan: (id, changes) => apiService.patch(`/superadmin/subscription-plans/${id}/`, changes),
  listStripeEvents: (params = {}) => apiService.get('/superadmin/stripe-events/', { params }),
  getRevenueOverview: () => apiService.get('/superadmin/revenue-overview/'),
  setAccountStorageOverride: (accountId, backend, autoFailover = false) =>
    apiService.post(`/superadmin/accounts/${accountId}/storage-override/`, {
      storage_backend_override: backend,
      auto_failover_enabled: autoFailover,
    }),

  // Phase 3 — Mock payment gateway (admin testing console)
  getMockConsole: () => apiService.get('/superadmin/mock-payments/console/'),
  simulateSubscriptionEvent: (subscriptionId, eventType) =>
    apiService.post(`/superadmin/mock-payments/${subscriptionId}/simulate/`, { event_type: eventType }),
  resetMockSubscription: (subscriptionId) =>
    apiService.post(`/superadmin/mock-payments/${subscriptionId}/reset/`),
  getPaymentGateway: () => apiService.get('/superadmin/payment-gateway/'),
  setPaymentGateway: (gateway) =>
    apiService.patch('/superadmin/payment-gateway/', { payment_gateway: gateway }),
}

/**
 * Contact Form API (Public)
 */
export const contactAPI = {
  submitContactMessage: (data) => apiService.post('/contact/', data),
  getMyContactMessages: () => apiService.get('/my-contact-messages/')
}

// Export axios instance for direct use if needed
export { api }
export default apiService
